import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { array, object, ObjectSchema, string, boolean } from 'yup'
import { stringSchema, numberSchema, numberFromStringSchema } from 'src/Configuration/validation/rules'
import { InProgressPhotovoltaicValidation, PhotovoltaicFormData } from '../../configurations/photovoltaic'
import { DirectionType, PowerOptimizerType } from '../../types/photovoltaic'

const MIN_NUMBER_ZERO = 0
const MODULE_COUNT_MIN_NUMBER = 1
const MODULE_TYPE_NAME_MAX_LENGTH = 255
const MAX_NUMBER = 1000
const MAXIMUM_ORIENTATION = 90

/**
 * This hook provides the validation schema for photovoltaic trackers using the custom defined tests and yup-validation-rules
 */
const usePhotovoltaicValidation = (): {
  inProgressValidationSchema: ObjectSchema<InProgressPhotovoltaicValidation>
  finalValidationSchema: ObjectSchema<PhotovoltaicFormData>
} => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)
  const numberSchemaWithTranslation = numberSchema(t)
  const numberFromStringSchemaWithTranslation = numberFromStringSchema(t)

  const inProgressValidationSchema: ObjectSchema<InProgressPhotovoltaicValidation> = object().shape({
    pvTrackers: array()
      .of(
        object().shape({
          name: stringSchemaWithTranslation({ maxLength: MODULE_TYPE_NAME_MAX_LENGTH }),
          pvStrings: array().of(
            object().shape({
              pvModuleType: stringSchemaWithTranslation({ maxLength: MODULE_TYPE_NAME_MAX_LENGTH }).default(''),
              moduleCount: numberSchemaWithTranslation({ integer: true, min: MODULE_COUNT_MIN_NUMBER, max: MAX_NUMBER }).required().nullable(),
              powerOptimizer: string<PowerOptimizerType | ''>().default(''),
              direction: string<DirectionType | ''>().default(''),
              orientation: numberSchemaWithTranslation({ max: MAXIMUM_ORIENTATION, min: MIN_NUMBER_ZERO }).required().nullable(),
              testVoltage: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required().nullable(),
              modulePeakPower: numberFromStringSchemaWithTranslation({ max: MAX_NUMBER }).required().nullable(),
              openCircuitVoltage: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required().nullable(),
              positiveElectrodeGround: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required().nullable(),
              negativeElectrodeGround: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required().nullable(),
              riso: numberSchemaWithTranslation({ max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required().nullable(),
            })
          ),
          appliedToAllString: boolean().required(),
        })
      )
      .required(),
  })

  const finalValidationSchema: ObjectSchema<PhotovoltaicFormData> = object().shape({
    pvTrackers: array()
      .of(
        object()
          .shape({
            name: stringSchemaWithTranslation({ maxLength: MODULE_TYPE_NAME_MAX_LENGTH }).required(),
            pvStrings: array()
              .of(
                object().shape({
                  pvModuleType: stringSchemaWithTranslation({ maxLength: MODULE_TYPE_NAME_MAX_LENGTH }).required(),
                  moduleCount: numberSchemaWithTranslation({ integer: true, min: MODULE_COUNT_MIN_NUMBER, max: MAX_NUMBER }).required(),
                  powerOptimizer: string<PowerOptimizerType | ''>().required(),
                  direction: string<DirectionType | ''>().required(),
                  orientation: numberSchemaWithTranslation({ max: MAXIMUM_ORIENTATION, min: MIN_NUMBER_ZERO }).required(),
                  testVoltage: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required(),
                  modulePeakPower: numberFromStringSchemaWithTranslation({ max: MAX_NUMBER }).required(),
                  openCircuitVoltage: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required(),
                  positiveElectrodeGround: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required(),
                  negativeElectrodeGround: numberSchemaWithTranslation({ integer: true, max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required(),
                  riso: numberSchemaWithTranslation({ max: MAX_NUMBER, min: MIN_NUMBER_ZERO }).required(),
                })
              )
              .required(),
            appliedToAllString: boolean().required(),
          })
          .required()
      )
      .required(),
  })

  return { inProgressValidationSchema, finalValidationSchema }
}

export default usePhotovoltaicValidation
